/**
 * One live connection with reconnect, clock offset and acknowledged commands.
 * Prefers a WebSocket; after repeated early failures (hotspots, proxies, flaky cellular) it
 * long-polls over plain HTTP and keeps probing for the WebSocket to return.
 * Lives outside React (StrictMode would otherwise open two sockets).
 */
import { versionAction, type VersionAction } from './version.ts';

export type Status = 'connecting' | 'open' | 'closed' | 'denied';

export interface Ack {
  ok: boolean;
  reason?: string;
}

type Listener = () => void;

interface Frame<V> { k: string; view?: V; id?: string; ok?: boolean; reason?: string; t?: number; serverNow?: number; phase?: string }

export class LiveSocket<V> {
  view: V | null = null;
  status: Status = 'connecting';
  reason: string | null = null;
  offset = 0; // serverNow - clientNow
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private pending = new Map<string, (a: Ack) => void>();
  private retry = 0;
  private pingTimer: number | null = null;
  private lastPong = Date.now();
  private stopped = false;
  private offsets: number[] = [];
  private serverVersion: unknown = null;
  private versionStatus: VersionAction = 'invalid';
  private mode: 'ws' | 'poll' = 'ws';
  private strikes = 0; // consecutive sockets that never delivered a version frame
  private reconnectTimer: number | null = null;
  private probeTimer: number | null = null;
  private pollCtl: AbortController | null = null;
  private pollN = -1;
  private subTable: number | null = null;
  private kicked = false; // a wake-up aborted the poll on purpose
  private readonly clientId = `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;

  /**
   * @param url    path + query of the socket endpoint
   * @param check  optional: asked after repeated failures whether the link is still valid;
   *               resolving false marks the socket 'denied' (seat released, link expired).
   */
  constructor(
    private readonly url: string,
    private readonly check?: () => Promise<boolean>,
  ) {
    this.connect();
    window.addEventListener('online', this.wake);
    document.addEventListener('visibilitychange', this.wake);
  }

  subscribe = (fn: Listener): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  private emit(): void {
    for (const l of this.listeners) l();
  }

  private connect(): void {
    if (this.stopped) return;
    this.serverVersion = null;
    this.versionStatus = 'invalid';
    this.status = 'connecting';
    this.emit();
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${location.host}${this.url}`);
    this.ws = ws;
    let usable = false; // true once the server's version frame arrives
    const guard = window.setTimeout(() => { if (!usable) ws.close(); }, 6000);
    ws.onopen = () => {
      if (this.ws !== ws || this.stopped) return;
      this.retry = 0;
      this.status = 'open';
      this.lastPong = Date.now();
      this.emit();
      this.ping();
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.pingTimer = window.setInterval(() => {
        if (Date.now() - this.lastPong > 35_000) ws.close();
        else this.ping();
      }, 15_000);
    };
    ws.onmessage = (ev) => {
      if (this.ws !== ws || this.stopped) return;
      let msg: Frame<V>;
      try {
        msg = JSON.parse(String(ev.data));
      } catch {
        return;
      }
      if (msg.k === 'version') usable = true;
      this.handle(msg);
    };
    ws.onclose = (ev) => {
      clearTimeout(guard);
      if (this.ws !== ws) return;
      if (!this.stopped) {
        this.serverVersion = null;
        this.versionStatus = 'invalid';
        window.dispatchEvent(new CustomEvent('nightshift-version', { detail: { action: 'invalid', build: null } }));
      }
      if (this.pingTimer) clearInterval(this.pingTimer);
      for (const [, fn] of this.pending) fn({ ok: false, reason: 'Connection lost. Try again.' });
      this.pending.clear();
      if (this.reason || ev.code === 4001 || ev.code === 1008) {
        // server refused (released seat / expired link)
        this.status = 'denied';
        this.emit();
        return;
      }
      this.strikes = usable ? 0 : this.strikes + 1; // a delivered version frame proves WebSockets work here
      if (this.strikes >= 2) {
        this.startPolling();
        return;
      }
      this.status = 'closed';
      this.emit();
      const wait = Math.min(8000, 500 * 2 ** this.retry++);
      // A refused upgrade looks like 1006 to the browser: ask the server why.
      if (this.check && this.retry >= 2) {
        this.check()
          .then((valid) => {
            if (valid) this.schedule(wait);
            else {
              this.status = 'denied';
              this.emit();
            }
          })
          .catch(() => this.schedule(wait));
        return;
      }
      this.schedule(wait);
    };
  }

  private schedule(wait: number): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = window.setTimeout(() => { this.reconnectTimer = null; this.connect(); }, wait);
  }

  /** One server frame, from either transport. */
  private handle(msg: Frame<V>): void {
    if (msg.k === 'version') {
      this.serverVersion = msg;
      this.checkVersion(msg.phase ?? '');
    } else if (msg.k === 'view') {
      this.checkVersion((msg.view as { clock?: { phase?: string } } | undefined)?.clock?.phase ?? '');
      if (this.versionStatus !== 'current') return;
      this.view = msg.view ?? null;
      if (msg.serverNow && this.offsets.length === 0) this.offset = msg.serverNow - Date.now();
      this.emit();
    } else if (msg.k === 'error') {
      this.reason = msg.reason ?? 'This session cannot be opened.';
      console.warn('Session connection refused:', this.reason);
      this.view = null;
      this.stopped = true;
      this.pollCtl?.abort();
      this.status = 'denied';
      this.emit();
    } else if (msg.k === 'ack' && msg.id) {
      this.pending.get(msg.id)?.({ ok: !!msg.ok, reason: msg.reason });
      this.pending.delete(msg.id);
    } else if (msg.k === 'pong' && msg.t && msg.serverNow) {
      this.lastPong = Date.now();
      const rtt = Date.now() - msg.t;
      this.offsets.push(msg.serverNow + rtt / 2 - Date.now());
      if (this.offsets.length > 5) this.offsets.shift();
      const sorted = [...this.offsets].sort((a, b) => a - b);
      this.offset = sorted[Math.floor(sorted.length / 2)]!;
    }
  }

  private get pollQuery(): string {
    return `${this.url.replace(/^\/ws/, '')}${this.url.includes('?') ? '&' : '?'}c=${this.clientId}`;
  }

  /** WebSockets keep failing: carry on over HTTP and probe for the socket to come back. */
  private startPolling(): void {
    console.warn('WebSocket unavailable on this network; using HTTP polling.');
    this.mode = 'poll';
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.ws = null;
    this.status = 'connecting';
    this.emit();
    void this.pollLoop();
    this.probeTimer = window.setInterval(() => this.probeSocket(), 60_000);
  }

  private probeSocket(): void {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const probe = new WebSocket(`${proto}://${location.host}${this.url}`);
    const giveUp = window.setTimeout(() => probe.close(), 6000);
    probe.onmessage = (ev) => {
      clearTimeout(giveUp);
      probe.close();
      if (this.stopped || this.mode !== 'poll' || !String(ev.data).includes('"version"')) return;
      this.stopPolling();
      this.strikes = 0;
      this.retry = 0;
      this.connect();
    };
    probe.onerror = () => clearTimeout(giveUp);
  }

  private stopPolling(): void {
    this.mode = 'ws';
    this.pollCtl?.abort();
    if (this.probeTimer) clearInterval(this.probeTimer);
    this.probeTimer = null;
  }

  private async pollLoop(): Promise<void> {
    let fails = 0;
    while (this.mode === 'poll' && !this.stopped) {
      const ctl = (this.pollCtl = new AbortController());
      const timer = window.setTimeout(() => ctl.abort(), 30_000);
      try {
        const res = await fetch(`/poll${this.pollQuery}&since=${this.pollN}&sub=${this.subTable ?? ''}`, { signal: ctl.signal, cache: 'no-store' });
        if (res.status === 401 || res.status === 404) {
          this.handle({ k: 'error', reason: ((await res.json().catch(() => ({}))) as { error?: string }).error ?? 'This link has expired or the seat was released.' });
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = (await res.json()) as { version: Frame<V>; n: number; frame: Frame<V> | null };
        if (this.mode !== 'poll' || this.stopped) return;
        fails = 0;
        this.pollN = body.n;
        this.handle(body.version);
        if (body.frame) this.handle(body.frame);
        if (this.status !== 'open') {
          this.status = 'open';
          this.emit();
        }
      } catch (e) {
        if (this.mode !== 'poll' || this.stopped) return;
        if (ctl.signal.aborted && !this.kicked) console.warn('Poll timed out; retrying.');
        else if (!ctl.signal.aborted) console.warn('Poll failed:', e);
        if (!this.kicked && ++fails >= 2 && this.status !== 'closed') {
          this.status = 'closed';
          this.emit();
        }
        if (!this.kicked) await new Promise((r) => window.setTimeout(r, Math.min(8000, 500 * 2 ** fails)));
      } finally {
        clearTimeout(timer);
        this.kicked = false;
      }
    }
  }

  private async post(id: string, cmd: unknown): Promise<Ack> {
    // The server drops repeats of the same id, so one retry cannot apply a command twice.
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const res = await fetch(`/cmd${this.pollQuery}`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ id, cmd }), signal: AbortSignal.timeout(8000) });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const ack = (await res.json()) as Ack;
        return { ok: !!ack.ok, reason: ack.reason };
      } catch (e) {
        console.warn('Command post failed:', e);
      }
    }
    return { ok: false, reason: 'No answer from the server.' };
  }

  /** Phone woke up or the network changed: do not sit out a backoff or trust a stale socket. */
  private wake = (): void => {
    if (this.stopped || document.visibilityState === 'hidden') return;
    if (this.mode === 'poll') {
      this.kicked = true;
      this.pollCtl?.abort();
    } else if (this.status === 'open' && Date.now() - this.lastPong > 20_000) this.ws?.close();
    else if (this.status === 'closed' && this.reconnectTimer) {
      this.retry = 0;
      this.schedule(0);
    }
  };

  private ping(): void {
    this.ws?.readyState === WebSocket.OPEN && this.ws.send(JSON.stringify({ k: 'ping', t: Date.now() }));
  }

  private checkVersion(phase: string): void {
    this.versionStatus = versionAction(this.serverVersion, phase);
    const build = this.versionStatus === 'invalid' ? null : (this.serverVersion as { build: string }).build;
    if (this.versionStatus === 'invalid') console.warn('Server sent a view without valid deployment metadata.');
    window.dispatchEvent(new CustomEvent('nightshift-version', { detail: { action: this.versionStatus, build } }));
  }

  /** Applies the current connection's command gate to HTTP mutations too. */
  get commandError(): string | null {
    if (this.reason) return this.reason;
    if (this.versionStatus !== 'current') return 'Reload to connect to the current version before sending commands.';
    if (this.mode === 'poll' ? this.status !== 'open' : !this.ws || this.ws.readyState !== WebSocket.OPEN) return 'Reconnecting — try again in a moment.';
    return null;
  }

  send(cmd: unknown): Promise<Ack> {
    const reason = this.commandError;
    if (reason) return Promise.resolve({ ok: false, reason });
    const id = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
    if (this.mode === 'poll') return this.post(id, cmd);
    return new Promise((resolve) => {
      this.pending.set(id, resolve);
      this.ws!.send(JSON.stringify({ k: 'cmd', id, cmd }));
      window.setTimeout(() => {
        if (this.pending.has(id)) {
          this.pending.delete(id);
          resolve({ ok: false, reason: 'No answer from the server.' });
        }
      }, 8000);
    });
  }

  sub(table: number | null): void {
    if (this.mode === 'poll' && table !== this.subTable) {
      this.subTable = table;
      this.kicked = true;
      this.pollCtl?.abort();
      return;
    }
    this.subTable = table;
    this.ws?.readyState === WebSocket.OPEN && this.ws.send(JSON.stringify({ k: 'sub', table }));
  }

  now(): number {
    return Date.now() + this.offset;
  }

  close(): void {
    this.stopped = true;
    window.removeEventListener('online', this.wake);
    document.removeEventListener('visibilitychange', this.wake);
    if (this.pingTimer) clearInterval(this.pingTimer);
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.stopPolling();
    this.ws?.close();
  }
}
